import { labels } from '@/app/constants/labels';
import ExpandMore from '@ixigo/icons/ExpandMore';
import Accordion from '@ixigo/iui/Accordion';
import AccordionBody from '@ixigo/iui/AccordionBody';
import AccordionHead from '@ixigo/iui/AccordionHead';
import Button from '@ixigo/iui/Button';
import Chip from '@ixigo/iui/Chip';
import Typography from '@ixigo/iui/Typography';
import { useEffect } from 'react';
import type { EmiOption } from '../../domain/payment';
import EmiPlanSelection from './EmiPlanSelection';
import NoEmiPlans from './NoEmiPlans';
import { emiScrollHelper } from './emiScrollHelper';
import type { EMIPlanAccordionProps } from './types';

const EMIPlanAccordion = ({
	emiOptions,
	onAddCardCTA,
	onBankChange,
	selectedBank,
	selectedEmiPlan,
	onSelectEmiPlan,
	type,
}: EMIPlanAccordionProps) => {
	useEffect(() => {
		if (!selectedEmiPlan) {
			onSelectEmiPlan(emiOptions[0].emiInfo[0]);
		}
	}, [selectedEmiPlan, onSelectEmiPlan, emiOptions]);

	if (!emiOptions.length) {
		return <NoEmiPlans type={type} />;
	}

	const onExpand = (bank: EmiOption, id: string, open: boolean) => {
		onBankChange(bank);
		// Only scroll when opening the accordion
		if (open) {
			emiScrollHelper(id, '.scrollable-container');
		}
	};

	return emiOptions.map((option) => {
		const { juspayBankCode, cardType, emiInfo } = option;
		const id = juspayBankCode + cardType;
		const startingFrom = emiInfo.reduce((min, curr) => {
			return Math.min(min, curr.monthlyInstalments);
		}, Number.POSITIVE_INFINITY);
		const isNoCostEmi = emiInfo.some((emi) => emi.interestRate === 0);
		return (
			<Accordion
				expanded={option === selectedBank}
				onChange={(open) => onExpand(option, id, open)}
				key={id}
			>
				<AccordionHead expandIcon={<ExpandMore />} className={'py-15 hover:bg-white'}>
					<div className="flex items-center gap-20" id={id}>
						<div style={{ backgroundImage: `url(${option.img})` }} className="h-40 w-40 bg-cover" />
						<div className="flex-1">
							<Typography variant="body-md" className="flex items-center gap-5 flex-wrap">
								{option.issuerName ?? option.bankCode}
								{isNoCostEmi && (
									<Chip color="success" size="xs" className="ml-5">
										{labels.NO_COST_EMI_TEXT}
									</Chip>
								)}
							</Typography>
							<Typography variant="body-xs" className="text-secondary" fontWeight="normal">
								{labels.EMI_STARTING_FROM_TEXT(startingFrom)}
							</Typography>
						</div>
					</div>
				</AccordionHead>
				<AccordionBody>
					<EmiPlanSelection
						emiPlans={emiInfo}
						selectedEmiPlan={selectedEmiPlan!}
						onSelectEmiPlan={onSelectEmiPlan}
						bankCode={juspayBankCode}
					>
						<Button
							data-testid={`Add Card ${id}`}
							onClick={onAddCardCTA}
							size="lg"
							className="mt-15 w-full"
						>
							{labels.ADD_CARD_CTA}
						</Button>
					</EmiPlanSelection>
				</AccordionBody>
			</Accordion>
		);
	});
};

export default EMIPlanAccordion;
